import type {DirectTurnKind, DirectTurnStartPath} from '@github-ui/ahp-session-client/contracts'
import {sendCustomMetric} from '@github-ui/stats'

import {commonTags, createEmitter, type MetricSender} from './browser-outcome-metrics'
import {modelFamily, type ModelFamily} from './model-family'
import {recordTaskStartType, recordTurnStartType, startTypeFromStartPath} from './start-type'
import {failed, NO_REASONS, unresolved, type OutcomeReasons, type UnresolvedReason} from './outcome-reasons'
import type {ResultPresentationFact} from './result-presentation-contract'
import {isOneCopilotBrowserTelemetryEnabled} from './telemetry-gate'
import type {UiReadinessRole, UiReadinessSurface} from './ui-readiness-contract'

export const TIME_TO_FIRST_CONTENT_PHASES = [
  'submit_to_dispatch',
  'dispatch_to_acknowledgement',
  'acknowledgement_to_first_content',
  'first_content_to_paint',
] as const
export type TimeToFirstContentPhase = (typeof TIME_TO_FIRST_CONTENT_PHASES)[number]
export type TurnTerminalOutcome = 'completed' | 'cancelled' | 'error'
export const ABANDON_KINDS = ['page_exit', 'navigation', 'panel_close', 'stop', 'other'] as const
export type AbandonKind = (typeof ABANDON_KINDS)[number]

// Surfaces that live inside another page can stay open across host navigations.
const PANEL_SURFACES: ReadonlySet<UiReadinessSurface> = new Set([
  'embedded_chat',
  'global_overlay',
  'dashboard',
  'issue_panel',
  'memex_panel',
  'repository_panel',
  'explain_error',
])

/** Whether the page is still showing the turn's task, or a new-task page that redirects to it. */
export function showsTask(pathname: string, taskId: string | undefined): boolean {
  if (/^\/copilot\/?$/.test(pathname) || /^\/[^/]+\/[^/]+\/agents\/?$/.test(pathname)) return true
  const match = /^\/(?:copilot|[^/]+\/[^/]+)\/tasks\/([^/]+)\/?$/.exec(pathname)
  return Boolean(match && (!taskId || match[1] === taskId))
}

export interface TurnMetricsPage {
  pathname(): string
  /** Subscribes to page navigations and exits. */
  subscribe(listener: {navigation(): void; pageExit(event?: PageTransitionEvent): void}): void
}

const browserPage: TurnMetricsPage | undefined =
  typeof window === 'undefined'
    ? undefined
    : {
        pathname: () => window.location.pathname,
        subscribe: listener => {
          window.addEventListener('pagehide', listener.pageExit)
          window.addEventListener('popstate', listener.navigation)
          // The Navigation API also reports history.pushState navigations where it is available.
          const navigation = (window as {navigation?: EventTarget}).navigation
          navigation?.addEventListener('currententrychange', listener.navigation)
          document.addEventListener('soft-nav:end', listener.navigation)
        },
      }

type CompletedPresentation = Extract<ResultPresentationFact, {observation: 'completed'}>
export type TurnMetricsOwner = object

/** Turn timing in `performance.timeOrigin + performance.now()` milliseconds. */
export interface TurnSubmission {
  readonly requestId: string
  readonly taskId?: string
  readonly surface?: UiReadinessSurface
  readonly turnKind: DirectTurnKind
  readonly startPath: DirectTurnStartPath
  readonly readyAtSubmit: boolean
  readonly modelId?: string
  readonly submittedAtMs: number
  /** False when submission timing came from an earlier document, such as a One Copilot entry. */
  readonly submittedInDocument: boolean
}

export interface TurnDispatch extends TurnSubmission {
  readonly turnId: string
  readonly dispatchedAtMs: number
  readonly attributionPending?: boolean
}

interface TurnRecord extends Omit<TurnDispatch, 'modelId' | 'taskId' | 'surface' | 'attributionPending'> {
  readonly modelFamily: ModelFamily
  readonly owner?: TurnMetricsOwner
  taskId?: string
  surface?: UiReadinessSurface
  attributionPending?: boolean
  surfaceObserved?: UiReadinessSurface
  role?: UiReadinessRole
  acknowledgedAtMs?: number
  firstContentAtMs?: number
  contentReceipt?: boolean
  ttftFinal?: boolean
  terminalOutcome?: TurnTerminalOutcome
  terminalAtMs?: number
  terminalEmitted?: boolean
  pendingPresentation?: CompletedPresentation
  pendingContentEnd?: {outcome: 'failure' | 'unresolved'; reasons: OutcomeReasons}
}

interface PendingTurnRecord extends Omit<TurnSubmission, 'modelId'> {
  readonly modelFamily: ModelFamily
  readonly owner?: TurnMetricsOwner
}

type TagSource = Omit<TurnSubmission, 'modelId'> & {
  readonly turnId?: string
  readonly modelFamily: ModelFamily
  readonly surfaceObserved?: UiReadinessSurface
  readonly role?: UiReadinessRole
}

const MAX_TURN_RECORDS = 256

function rememberFinalizedRequest(finalized: Set<string>, requestId: string) {
  finalized.add(requestId)
  if (finalized.size <= MAX_TURN_RECORDS) return
  const oldest = finalized.values().next().value
  if (oldest) finalized.delete(oldest)
}

export function createTurnMetrics({
  send = sendCustomMetric,
  reportError = () => undefined,
  timeOrigin = () => performance.timeOrigin,
  enabled = isOneCopilotBrowserTelemetryEnabled,
  page = browserPage,
}: {
  send?: MetricSender
  reportError?: () => void
  timeOrigin?: () => number
  enabled?: () => boolean
  page?: TurnMetricsPage
} = {}) {
  const emit = createEmitter(send, reportError)
  const pending = new Map<string, PendingTurnRecord>()
  const records = new Map<string, TurnRecord>()
  const finalizedRequests = new Set<string>()
  let pageInstalled = false

  const turnTags = (record: TagSource) => ({
    ...commonTags(
      {
        surface: record.surfaceObserved ?? record.surface ?? 'unknown',
        role: record.role ?? 'unknown',
        task_id: record.taskId,
      },
      {startType: startTypeFromStartPath(record.startPath)},
    ),
    turn_kind: record.turnKind,
    model_family: record.modelFamily,
  })
  // A turn that cold-starts or resumes reports how the task's connection started; an active turn
  // reuses that connection.
  const recordStart = (record: Pick<TurnDispatch, 'turnId' | 'taskId' | 'startPath'>) => {
    recordTurnStartType(record.turnId, startTypeFromStartPath(record.startPath))
    if (record.startPath !== 'active') recordTaskStartType(record.taskId, startTypeFromStartPath(record.startPath))
  }
  const ttftTags = (record: TagSource) => ({
    ...turnTags(record),
    ready_at_submit: record.readyAtSubmit ? 'ready' : 'waiting',
  })
  const rum = (record: TagSource) => ({
    ...(record.turnId ? {turn_id: record.turnId} : {}),
    request_id: record.requestId,
  })
  const emitTtftOutcome = (record: TagSource, outcome: 'success' | 'failure' | 'unresolved', reasons: OutcomeReasons) =>
    emit(
      {
        name: 'ONE_COPILOT_WEB_TIME_TO_FIRST_CONTENT_OUTCOME',
        value: 1,
        tags: {...ttftTags(record), outcome, ...reasons},
      },
      {task_id: record.taskId},
      rum(record),
    )

  const release = (record: TurnRecord) => {
    if (record.ttftFinal && record.terminalEmitted) records.delete(record.turnId)
  }
  const emitTerminal = (record: TurnRecord) => {
    if (
      record.attributionPending ||
      record.terminalEmitted ||
      !record.terminalOutcome ||
      record.terminalAtMs === undefined
    )
      return
    record.terminalEmitted = true
    const elapsed = record.terminalAtMs - record.submittedAtMs
    if (Number.isFinite(elapsed) && elapsed >= 0) {
      emit(
        {
          name: 'ONE_COPILOT_WEB_TURN_LATENCY',
          value: elapsed,
          tags: {...turnTags(record), outcome: record.terminalOutcome},
        },
        {task_id: record.taskId},
        rum(record),
      )
    }
    release(record)
  }
  // Abandonment is the subset of unresolved first-content outcomes where the user left or stopped.
  const abandonKind = (record: TurnRecord, reason: UnresolvedReason): AbandonKind | undefined => {
    if (reason === 'page_exit') return 'page_exit'
    if (reason === 'cancelled') return 'stop'
    if (reason !== 'disposed') return undefined
    const surface = record.surfaceObserved ?? record.surface
    if (!surface || surface === 'unknown') return 'other'
    return PANEL_SURFACES.has(surface) ? 'panel_close' : 'navigation'
  }
  const finalizeUnresolved = (record: TurnRecord, reason: UnresolvedReason) => {
    if (record.ttftFinal) return
    record.ttftFinal = true
    emitTtftOutcome(record, 'unresolved', unresolved(reason))
    const kind = abandonKind(record, reason)
    if (kind) {
      emit(
        {name: 'ONE_COPILOT_WEB_TURN_ABANDONED', value: 1, tags: {...ttftTags(record), abandon_kind: kind}},
        {task_id: record.taskId},
        rum(record),
      )
    }
  }
  const finalizePending = (record: PendingTurnRecord, outcome: 'failure' | 'unresolved', reasons: OutcomeReasons) => {
    if (finalizedRequests.has(record.requestId)) return
    pending.delete(record.requestId)
    rememberFinalizedRequest(finalizedRequests, record.requestId)
    emitTtftOutcome(record, outcome, reasons)
  }
  const retire = (record: TurnRecord, fallback: UnresolvedReason) => {
    if (record.ttftFinal) return
    if (record.firstContentAtMs !== undefined || record.contentReceipt) {
      finalizeUnresolved(record, 'missing_evidence')
    } else if (record.terminalOutcome === 'error') {
      record.ttftFinal = true
      emitTtftOutcome(record, 'failure', failed('turn_error'))
    } else if (record.terminalOutcome === 'cancelled') {
      finalizeUnresolved(record, 'cancelled')
    } else if (record.terminalOutcome === 'completed') {
      finalizeUnresolved(record, 'no_content')
    } else {
      finalizeUnresolved(record, fallback)
    }
  }
  const present = (record: TurnRecord, completion: CompletedPresentation) => {
    if (record.ttftFinal) return
    if (record.attributionPending) {
      record.pendingPresentation = completion
      return
    }
    record.ttftFinal = true
    record.surfaceObserved = completion.surface
    record.role = completion.role
    const presentedAtMs = timeOrigin() + completion.closure_time_ms
    const elapsed = presentedAtMs - record.submittedAtMs
    if (!Number.isFinite(elapsed) || elapsed < 0) {
      emitTtftOutcome(record, 'unresolved', unresolved('other'))
      release(record)
      return
    }
    const fact = {task_id: record.taskId}
    emit(
      {name: 'ONE_COPILOT_WEB_TIME_TO_FIRST_CONTENT_LATENCY', value: elapsed, tags: ttftTags(record)},
      fact,
      rum(record),
    )
    const phases: Array<[TimeToFirstContentPhase, number | undefined, number | undefined]> = [
      ['submit_to_dispatch', record.submittedInDocument ? record.submittedAtMs : undefined, record.dispatchedAtMs],
      ['dispatch_to_acknowledgement', record.dispatchedAtMs, record.acknowledgedAtMs],
      ['acknowledgement_to_first_content', record.acknowledgedAtMs, record.firstContentAtMs],
      ['first_content_to_paint', record.firstContentAtMs, presentedAtMs],
    ]
    for (const [phase, start, end] of phases) {
      if (start === undefined || end === undefined || end < start) continue
      emit(
        {
          name: 'ONE_COPILOT_WEB_TIME_TO_FIRST_CONTENT_PHASE_LATENCY',
          value: end - start,
          tags: {...ttftTags(record), phase},
        },
        fact,
        rum(record),
      )
    }
    emitTtftOutcome(record, 'success', NO_REASONS)
    release(record)
  }
  const endContent = (record: TurnRecord, outcome: 'failure' | 'unresolved', reasons: OutcomeReasons) => {
    if (record.ttftFinal) return
    if (record.attributionPending) {
      record.pendingContentEnd = {outcome, reasons}
      return
    }
    if (outcome === 'unresolved') {
      finalizeUnresolved(record, reasons.unresolved_reason)
    } else {
      record.ttftFinal = true
      emitTtftOutcome(record, outcome, reasons)
    }
    release(record)
  }
  const resolveAttribution = (record: TurnRecord) => {
    record.attributionPending = false
    emitTerminal(record)
    const presentation = record.pendingPresentation
    if (presentation) {
      record.pendingPresentation = undefined
      present(record, presentation)
      return
    }
    const contentEnd = record.pendingContentEnd
    if (contentEnd) {
      record.pendingContentEnd = undefined
      endContent(record, contentEnd.outcome, contentEnd.reasons)
    }
  }
  const onPageExit = (event?: PageTransitionEvent) => {
    if (event?.persisted) return
    for (const record of pending.values()) finalizePending(record, 'unresolved', unresolved('page_exit'))
    for (const record of records.values()) {
      if (record.attributionPending) resolveAttribution(record)
      // Turns with a content receipt are finalized by their presentation view on the same page exit.
      if (!record.contentReceipt) retire(record, 'page_exit')
    }
    pending.clear()
  }
  const installPage = () => {
    if (pageInstalled || !page) return
    pageInstalled = true
    page.subscribe({navigation: onNavigation, pageExit: onPageExit})
  }
  const onNavigation = () => {
    if (!page) return
    const pathname = page.pathname()
    for (const record of records.values()) {
      const surface = record.surfaceObserved ?? record.surface
      if (record.contentReceipt || record.terminalOutcome || (surface && PANEL_SURFACES.has(surface))) continue
      if (!showsTask(pathname, record.taskId)) finalizeUnresolved(record, 'disposed')
    }
  }
  const record = (turnId: string | undefined) => (turnId ? records.get(turnId) : undefined)

  return {
    submitted(submission: TurnSubmission, owner?: TurnMetricsOwner) {
      if (!submission.requestId || !Number.isFinite(submission.submittedAtMs) || !enabled()) return
      finalizedRequests.delete(submission.requestId)
      installPage()
      const {modelId, ...rest} = submission
      pending.delete(submission.requestId)
      pending.set(submission.requestId, {...rest, modelFamily: modelFamily(modelId), owner})
      while (pending.size > MAX_TURN_RECORDS) {
        const oldest = pending.values().next().value
        if (!oldest) break
        finalizePending(oldest, 'unresolved', unresolved('other'))
      }
    },
    discarded(requestId: string) {
      if (!pending.delete(requestId)) return
      rememberFinalizedRequest(finalizedRequests, requestId)
    },
    disposed(owner?: TurnMetricsOwner) {
      for (const pendingRecord of pending.values()) {
        if (owner && pendingRecord.owner !== owner) continue
        finalizePending(pendingRecord, 'unresolved', unresolved('disposed'))
      }
      for (const [turnId, turnRecord] of records) {
        if (owner && turnRecord.owner !== owner) continue
        if (turnRecord.attributionPending) resolveAttribution(turnRecord)
        if (!turnRecord.ttftFinal) finalizeUnresolved(turnRecord, 'disposed')
        records.delete(turnId)
      }
    },
    dispatched(dispatch: TurnDispatch, owner?: TurnMetricsOwner) {
      if (!dispatch.turnId || !Number.isFinite(dispatch.submittedAtMs) || !enabled()) return
      if (finalizedRequests.has(dispatch.requestId)) return
      installPage()
      pending.delete(dispatch.requestId)
      const {modelId, ...rest} = dispatch
      records.delete(dispatch.turnId)
      records.set(dispatch.turnId, {...rest, modelFamily: modelFamily(modelId), owner})
      recordStart(dispatch)
      while (records.size > MAX_TURN_RECORDS) {
        const oldest = records.values().next().value
        if (!oldest) break
        if (oldest.attributionPending) resolveAttribution(oldest)
        retire(oldest, 'other')
        records.delete(oldest.turnId)
      }
    },
    attributed(turnId: string, taskId: string | undefined, surface: UiReadinessSurface | undefined) {
      const current = record(turnId)
      if (!current) return
      if (taskId) {
        current.taskId = taskId
        recordStart(current)
      }
      if (surface) current.surface = surface
      resolveAttribution(current)
    },
    preDispatchFailed(submission: TurnSubmission, owner?: TurnMetricsOwner) {
      if (!enabled() || finalizedRequests.has(submission.requestId)) return
      const existing = pending.get(submission.requestId)
      if (existing && (!owner || existing.owner === owner)) {
        finalizePending(existing, 'failure', failed('dispatch'))
        return
      }
      const {modelId, ...rest} = submission
      finalizePending({...rest, modelFamily: modelFamily(modelId), owner}, 'failure', failed('dispatch'))
    },
    acknowledged(turnId: string, atMs: number) {
      const current = record(turnId)
      if (current && current.acknowledgedAtMs === undefined) current.acknowledgedAtMs = atMs
    },
    firstContent(turnId: string, atMs: number) {
      const current = record(turnId)
      if (!current || current.firstContentAtMs !== undefined) return
      current.firstContentAtMs = atMs
    },
    terminal(turnId: string, outcome: TurnTerminalOutcome, atMs: number) {
      const current = record(turnId)
      if (!current || current.terminalOutcome) return
      current.terminalOutcome = outcome
      current.terminalAtMs = atMs
      emitTerminal(current)
    },
    contentReceipt(fact: ResultPresentationFact) {
      const current = record(fact.turn_id)
      if (!current) return
      current.contentReceipt = true
      current.surfaceObserved = fact.surface
      current.role = fact.role
    },
    // The end is the first content part visibly presented, not its acceptance into client state.
    contentPresented(completion: CompletedPresentation) {
      const current = record(completion.turn_id)
      if (current) present(current, completion)
    },
    contentEnded(turnId: string, outcome: 'failure' | 'unresolved', reasons: OutcomeReasons) {
      const current = record(turnId)
      if (current) endContent(current, outcome, reasons)
    },
    /**
     * The view showing the turn was replaced, for example by the task page after a redirect. The
     * next view's receipt can still finish the turn; a finished turn has nothing left to show.
     */
    contentReleased(turnId: string) {
      const current = record(turnId)
      if (!current || current.ttftFinal) return
      current.contentReceipt = false
      if (current.terminalOutcome) {
        finalizeUnresolved(current, 'disposed')
        release(current)
      }
    },
    pageExit: onPageExit,
  }
}

export type TurnMetrics = ReturnType<typeof createTurnMetrics>

export const turnMetrics = createTurnMetrics()
